import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { useEventStore } from "../../store/event";
import { GET_EVENT_SUMMARY, GET_EVENT_MENU_VENDOR_MISMATCHES } from "./queries";
import { EventDevicesSection } from "./EventDevicesSection";
import {
  EventConfigDiscrepanciesSection,
  type AttachmentRow,
} from "./EventConfigDiscrepanciesSection";

const client = createApolloClient();

// ===== Types =====
type PickerEvent = {
  id: number;
  name: string;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
};

type EventSummaryData = {
  id: number;
  name: string;
  is_active: boolean;
  currency: string | null;
  timezone: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  address_street: string | null;
  address_street_2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zipcode: string | null;
  organization: { id: number; name: string } | null;
  payment_processor_config: {
    id: number;
    processor: string | null;
    processing_rate: number | null;
    is_rfid: boolean;
    is_active: boolean;
  } | null;
  ticketing_payment_processor_id: number | null;
  ticketing_payment_processor_config: {
    id: number;
    processor: string | null;
    processing_rate: number | null;
  } | null;
  invoicing_payment_processor_id: number | null;
  invoicing_payment_processor_config: {
    id: number;
    processor: string | null;
  } | null;
  ticketing_service_fee_per_order: number | null;
  top_up_service_fee_per_order: number | null;
  top_up_service_fee_percentage: number | null;
  web_surcharge_percentage: number | null;
  cashless_top_up_sales: boolean | null;
  open_loop_cashless: boolean | null;
  available_tokens: string[] | null;
  redeemable_tokens: string[] | null;
  locations: Array<{
    id: number;
    name: string;
    is_active: boolean;
    vendor_id: number | null;
    vendor: { id: number; name: string; is_active: boolean } | null;
    payment_processor_id: number | null;
    payment_processor_config: {
      id: number;
      processor: string | null;
      payment_processor: string | null;
    } | null;
  }>;
  attendees_aggregate: { aggregate: { count: number } | null };
  rfid_assets_aggregate: {
    aggregate: {
      count: number;
      sum: { cash_balance: number | null; promo_balance: number | null } | null;
    } | null;
  };
  rfid_active: { aggregate: { count: number } | null };
  rfid_qr: { aggregate: { count: number } | null };
  rfid_access_only: { aggregate: { count: number } | null };
};

// ===== Formatters =====
const fmtMoneyCents = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
const fmtPercent = (n: number | null | undefined) =>
  n == null ? "—" : `${Number(n)}%`;
const fmtMoneyDollars = (n: number | null | undefined) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const fmtBool = (b: boolean | null | undefined) =>
  b == null ? "—" : b ? "Yes" : "No";
const fmtDate = (d: string | null) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};
const buildAddress = (e: EventSummaryData) => {
  const parts = [
    e.address_street,
    e.address_street_2,
    [e.address_city, e.address_state].filter(Boolean).join(", "),
    e.address_zipcode,
  ].filter((s) => s && s.trim().length > 0);
  return parts.length > 0 ? parts.join(" • ") : "—";
};
const tokenLabel = (tokens: string[] | null) =>
  tokens && tokens.length > 0 ? tokens.join(", ") : "—";

// ===== Layout helpers =====
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-3 gap-3 py-2 border-b border-gray-100 last:border-b-0">
    <dt className="col-span-1 text-sm font-medium text-gray-500">{label}</dt>
    <dd className="col-span-2 text-sm text-gray-900 break-words">{value}</dd>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 print:shadow-none print:border-gray-300 break-inside-avoid">
    <h2 className="text-base font-semibold text-gray-900 mb-3">{title}</h2>
    <dl>{children}</dl>
  </section>
);

// Several Sections stacked into one card with tabs. On screen only the active
// tab renders; in print every panel renders with its own heading, so the
// printed summary stays complete.
const TabbedSection = ({
  tabs,
}: {
  tabs: { title: string; content: React.ReactNode }[];
}) => {
  const [active, setActive] = useState(0);
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 print:shadow-none print:border-gray-300 break-inside-avoid">
      <div className="flex gap-1 mb-3 border-b border-gray-200 print:hidden">
        {tabs.map((t, i) => (
          <button
            key={t.title}
            type="button"
            onClick={() => setActive(i)}
            className={`-mb-px px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors ${
              i === active
                ? "border-red-700 text-red-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.title}
          </button>
        ))}
      </div>
      {tabs.map((t, i) => (
        <div key={t.title} className={i === active ? "" : "hidden print:block"}>
          <h2 className="hidden print:block text-base font-semibold text-gray-900 mb-3 mt-4 first:mt-0">
            {t.title}
          </h2>
          <dl>{t.content}</dl>
        </div>
      ))}
    </section>
  );
};

const Stat = ({
  label,
  value,
  color = "text-gray-900",
  plain,
}: {
  label: string;
  value: string;
  color?: string;
  plain?: boolean;
}) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </span>
    <span
      className={`text-lg font-bold ${color} ${plain ? "" : "tabular-nums"}`}
    >
      {value}
    </span>
  </div>
);

// ===== Date helpers (URL <-> Date) =====
const todayISO = () => new Date().toISOString().slice(0, 10);

const dateToInclusiveEndISO = (yyyymmdd: string) => {
  // _lt: end+1 so the range is inclusive on the end date.
  const d = new Date(`${yyyymmdd}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
};
const dateToStartISO = (yyyymmdd: string) =>
  new Date(`${yyyymmdd}T00:00:00`).toISOString();

// ===== Page =====
export const EventSummaryHubPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const headerEventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);

  const fromParam = searchParams.get("from") ?? "";
  const toParam = searchParams.get("to") ?? "";
  const selectedEventId = headerEventId ? Number(headerEventId) : null;

  const [summary, setSummary] = useState<EventSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Menu↔location attachments, fetched once per event and shared by the
  // vendor-header status coloring and the Menu Ownership Check section.
  const [attachments, setAttachments] = useState<AttachmentRow[] | null>(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);

  const selectedEvent = useMemo<PickerEvent | null>(
    () => {
      const e = (allEvents ?? []).find((evt: any) => Number(evt?.id) === selectedEventId);
      return e ? {
        id: Number(e.id),
        name: String(e.name ?? ""),
        start_date: e.start_date ?? null,
        end_date: e.end_date ?? null,
        is_active: e.is_active ?? true,
      } : null;
    },
    [allEvents, selectedEventId]
  );

  // Default the date range to the selected event's own start/end on first load.
  useEffect(() => {
    if (!selectedEvent) return;
    const next = new URLSearchParams(searchParams);
    let touched = false;
    if (!fromParam && selectedEvent.start_date) {
      next.set("from", selectedEvent.start_date);
      touched = true;
    }
    if (!toParam && selectedEvent.end_date) {
      next.set("to", selectedEvent.end_date);
      touched = true;
    }
    if (touched) setSearchParams(next, { replace: true });
    // We intentionally don't depend on searchParams setters to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);

  // Fetch summary whenever the event or date range changes.
  useEffect(() => {
    if (!selectedEventId || !fromParam || !toParam) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const { data } = await client.query<{
          events_by_pk: EventSummaryData;
        }>({
          query: GET_EVENT_SUMMARY,
          variables: {
            id: selectedEventId,
            from: dateToStartISO(fromParam),
            to: dateToInclusiveEndISO(toParam),
          },
          fetchPolicy: "network-only",
        });
        if (!cancelled) setSummary(data.events_by_pk);
      } catch (e: any) {
        if (!cancelled) {
          setSummaryError(e?.message ?? "Failed to load summary");
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, fromParam, toParam]);

  // Fetch menu↔location attachments per event (menu setup isn't date-scoped).
  useEffect(() => {
    if (!selectedEventId) {
      setAttachments(null);
      return;
    }
    let cancelled = false;
    setAttachmentsLoading(true);
    setAttachmentsError(null);
    client
      .query<{ location_menu: AttachmentRow[] }>({
        query: GET_EVENT_MENU_VENDOR_MISMATCHES,
        variables: { id: selectedEventId },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (!cancelled) setAttachments(data.location_menu);
      })
      .catch((e: any) => {
        if (!cancelled)
          setAttachmentsError(e?.message ?? "Failed to check menu ownership");
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  // When the header event changes, clear the existing from/to so the date
  // range re-defaults to the new event's configured dates on the next render.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let touched = false;
    if (next.has("from")) { next.delete("from"); touched = true; }
    if (next.has("to"))   { next.delete("to");   touched = true; }
    if (touched) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  // ===== Date filter handlers =====
  const setFrom = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("from", v);
    else next.delete("from");
    setSearchParams(next, { replace: true });
  };
  const setTo = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("to", v);
    else next.delete("to");
    setSearchParams(next, { replace: true });
  };

  // ===== Copy / print =====
  const handleCopy = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(buildPlainText(summary, fromParam, toParam));
      toast.success("Summary copied to clipboard");
    } catch (e: any) {
      toast.error("Couldn't copy: " + (e?.message ?? ""));
    }
  };
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-gray-100 p-4 print:bg-white print:p-0">
      {/* Filters card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 print:hidden">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900">Event Summary</h1>
          <div className="flex items-center gap-2">
            {summary && (
              <>
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 text-sm font-semibold rounded-lg shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Copy summary
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 text-sm font-semibold rounded-lg shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Print
                </button>
                <Link
                  to={`/events/${summary.id}/edit`}
                  className="px-4 py-2 text-sm font-semibold rounded-lg shadow-sm bg-red-700 text-white hover:bg-red-800 transition-colors"
                >
                  Edit Event
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              From
            </label>
            <input
              type="date"
              value={fromParam}
              onChange={(e) => setFrom(e.target.value)}
              max={toParam || todayISO()}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-600 focus:ring-red-600"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              To
            </label>
            <input
              type="date"
              value={toParam}
              onChange={(e) => setTo(e.target.value)}
              min={fromParam || undefined}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-600 focus:ring-red-600"
            />
          </div>
        </div>
        {selectedEvent && (
          <p className="mt-2 text-xs text-gray-500">
            Defaulted to {selectedEvent.name}'s configured dates ({fmtDate(selectedEvent.start_date)} → {fmtDate(selectedEvent.end_date)}). Override above to scope a single instance when the event is reused.
          </p>
        )}
      </div>

      {/* Empty / loading / error states */}
      {!selectedEventId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
          Select an organization and event in the header to see the summary.
        </div>
      )}
      {summaryError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {summaryError}
        </div>
      )}
      {summaryLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {/* Summary content */}
      {summary && !summaryLoading && (
        <SummaryContent
          event={summary}
          fromParam={fromParam}
          toParam={toParam}
          attachments={attachments}
        />
      )}

      {/* Menu ownership discrepancies */}
      {summary && !summaryLoading && (
        <EventConfigDiscrepanciesSection
          rows={attachments ?? []}
          loading={attachmentsLoading}
          error={attachmentsError}
        />
      )}

      {/* Device assignment + health */}
      {selectedEventId && !summaryLoading && (
        <EventDevicesSection
          eventId={selectedEventId}
          fromISO={fromParam ? dateToStartISO(fromParam) : null}
          toISO={toParam ? dateToInclusiveEndISO(toParam) : null}
        />
      )}
    </div>
  );
};

// ===== Vendor group status (colors the vendor headers) =====
// Label for the processor a location charges through. payment_processor is the
// display name ("Stripe"), processor the key ("stripe"); either may be null.
const locationProcessorLabel = (
  cfg: { id: number; processor: string | null; payment_processor: string | null } | null
) =>
  cfg
    ? `${cfg.payment_processor ?? cfg.processor ?? "Processor"} (#${cfg.id})`
    : "No processor";

// good  = vendor active, all locations active, menus owned by this vendor
// inactive = vendor and all its locations are off — cleanly dormant
// warning = menu owned by a different vendor, vendor/location active-state
//           disagreement, or no vendor assigned
type VendorGroupTone = "good" | "inactive" | "warning";

const TONE_STYLES: Record<
  VendorGroupTone,
  { header: string; chip: string; label: string }
> = {
  good: {
    header: "bg-green-100 border-green-200",
    chip: "bg-green-200 text-green-900",
    label: "OK",
  },
  inactive: {
    header: "bg-gray-100 border-gray-200",
    chip: "bg-gray-200 text-gray-600",
    label: "Inactive",
  },
  warning: {
    header: "bg-amber-100 border-amber-200",
    chip: "bg-amber-200 text-amber-900",
    label: "Review",
  },
};

const vendorGroupStatus = (
  group: {
    vendorId: number;
    locations: EventSummaryData["locations"];
  },
  attachments: AttachmentRow[] | null
): { tone: VendorGroupTone; reasons: string[] } => {
  const reasons = new Set<string>();
  const vendor = group.locations.find((l) => l.vendor)?.vendor ?? null;
  const activeLocs = group.locations.filter((l) => l.is_active);
  const inactiveLocs = group.locations.filter((l) => !l.is_active);

  if (group.vendorId === 0) {
    reasons.add("No vendor assigned to these locations");
  }

  // Menus attached to this vendor's locations but owned by someone else.
  if (attachments) {
    const locIds = new Set(group.locations.map((l) => l.id));
    for (const a of attachments) {
      if (!a.location || !a.menu || !locIds.has(a.location.id)) continue;
      if (a.menu.vendor_id === a.location.vendor_id) continue;
      const owner = a.menu.vendor;
      const ownerLabel =
        a.menu.vendor_id == null
          ? "no vendor"
          : `${owner?.name?.trim() || "?"} (#${a.menu.vendor_id})${
              owner && !owner.is_active ? ", inactive" : ""
            }`;
      reasons.add(`Menu "${a.menu.name}" owned by ${ownerLabel}`);
    }
  }

  const vendorInactive = vendor != null && !vendor.is_active;
  if (vendorInactive && activeLocs.length > 0) {
    reasons.add(
      `Vendor inactive but ${activeLocs.length} active location${
        activeLocs.length === 1 ? "" : "s"
      }`
    );
  }
  if (vendor != null && vendor.is_active && inactiveLocs.length > 0) {
    reasons.add(
      `${inactiveLocs.length} of ${group.locations.length} location${
        group.locations.length === 1 ? "" : "s"
      } inactive`
    );
  }

  if (reasons.size > 0) return { tone: "warning", reasons: [...reasons] };
  if (vendorInactive) return { tone: "inactive", reasons: [] };
  return { tone: "good", reasons: [] };
};

// ===== Summary content (the rendered event summary itself) =====
const SummaryContent = ({
  event,
  fromParam,
  toParam,
  attachments,
}: {
  event: EventSummaryData;
  fromParam: string;
  toParam: string;
  attachments: AttachmentRow[] | null;
}) => {
  const [locationsExpanded, setLocationsExpanded] = useState(false);

  const locationsByVendor = useMemo(() => {
    const map = new Map<
      number,
      { vendorId: number; vendorName: string; locations: typeof event.locations }
    >();
    for (const loc of event.locations) {
      const vid = loc.vendor_id ?? 0;
      const vname = loc.vendor?.name ?? "(no vendor assigned)";
      if (!map.has(vid)) {
        map.set(vid, { vendorId: vid, vendorName: vname, locations: [] });
      }
      map.get(vid)!.locations.push(loc);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.vendorName.localeCompare(b.vendorName)
    );
  }, [event.locations]);

  const groupStatuses = useMemo(
    () =>
      new Map(
        locationsByVendor.map((g) => [g.vendorId, vendorGroupStatus(g, attachments)])
      ),
    [locationsByVendor, attachments]
  );
  const flaggedVendorCount = [...groupStatuses.values()].filter(
    (s) => s.tone === "warning"
  ).length;

  const distinctVendorCount = locationsByVendor.filter(
    (g) => g.vendorId !== 0
  ).length;
  const activeLocationsCount = event.locations.filter((l) => l.is_active).length;
  const cashlessTotal = event.rfid_assets_aggregate.aggregate?.count ?? 0;
  const cashlessActive = event.rfid_active.aggregate?.count ?? 0;
  const cashLoadedCents =
    event.rfid_assets_aggregate.aggregate?.sum?.cash_balance ?? 0;

  const processorLine = (
    cfg: { id?: number; processor?: string | null } | null | undefined
  ) =>
    cfg
      ? `${cfg.processor ?? "—"}${cfg.id != null ? ` (#${cfg.id})` : ""}`
      : "—";

  return (
    <>
      {/* Header + stats bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 print:shadow-none print:border-gray-300">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{event.name}</h2>
              <span className="text-sm font-medium text-gray-500">
                #{event.id}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  event.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {event.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            {event.organization && (
              <p className="mt-1 text-sm font-medium text-gray-500">
                {event.organization.name}{" "}
                <span className="text-gray-400">#{event.organization.id}</span>
              </p>
            )}
            <p className="mt-1 text-xs text-gray-400">
              Date range: {fmtDate(fromParam)} → {fmtDate(toParam)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat
            label="Distinct Vendors"
            value={String(distinctVendorCount)}
            plain
          />
          <Stat
            label="Locations"
            value={`${event.locations.length} (${activeLocationsCount} active)`}
            plain
          />
          <Stat
            label="Attendees"
            value={(
              event.attendees_aggregate.aggregate?.count ?? 0
            ).toLocaleString()}
            plain
          />
          <Stat
            label="Cashless Assets"
            value={`${cashlessTotal.toLocaleString()} (${cashlessActive.toLocaleString()} active)`}
            plain
          />
          <Stat
            label="Total Loaded"
            value={fmtMoneyCents(cashLoadedCents)}
            color="text-green-700"
          />
        </div>
      </div>

      <TabbedSection
        tabs={[
          {
            title: "Payout & fees",
            content: (
              <>
                <Row
                  label="Primary processor"
                  value={processorLine(event.payment_processor_config)}
                />
                <Row
                  label="Processing rate"
                  value={fmtPercent(
                    event.payment_processor_config?.processing_rate ?? null
                  )}
                />
                <Row
                  label="Web surcharge"
                  value={fmtPercent(event.web_surcharge_percentage)}
                />
                <Row
                  label="Ticketing service fee / order"
                  value={fmtMoneyDollars(event.ticketing_service_fee_per_order)}
                />
                <Row
                  label="Top-up service fee / order"
                  value={fmtMoneyDollars(event.top_up_service_fee_per_order)}
                />
                <Row
                  label="Top-up service fee %"
                  value={fmtPercent(event.top_up_service_fee_percentage)}
                />
                {event.ticketing_payment_processor_id &&
                event.ticketing_payment_processor_id !==
                  event.payment_processor_config?.id ? (
                  <Row
                    label="Ticketing processor"
                    value={processorLine(event.ticketing_payment_processor_config)}
                  />
                ) : null}
                {event.invoicing_payment_processor_id ? (
                  <Row
                    label="Invoicing processor"
                    value={processorLine(event.invoicing_payment_processor_config)}
                  />
                ) : null}
              </>
            ),
          },
          {
            title: "Cashless config",
            content: (
              <>
                <Row
                  label="Top-up sales enabled"
                  value={fmtBool(event.cashless_top_up_sales)}
                />
                <Row
                  label="Open-loop cashless"
                  value={fmtBool(event.open_loop_cashless)}
                />
                <Row
                  label="Available tokens"
                  value={tokenLabel(event.available_tokens)}
                />
                <Row
                  label="Redeemable tokens"
                  value={tokenLabel(event.redeemable_tokens)}
                />
              </>
            ),
          },
          {
            title: "Cashless assets in date range",
            content: (
              <>
                <Row
                  label="Total assets"
                  value={cashlessTotal.toLocaleString()}
                />
                <Row label="Active" value={cashlessActive.toLocaleString()} />
                <Row
                  label="QR-based"
                  value={(event.rfid_qr.aggregate?.count ?? 0).toLocaleString()}
                />
                <Row
                  label="Access-only"
                  value={(
                    event.rfid_access_only.aggregate?.count ?? 0
                  ).toLocaleString()}
                />
                <Row
                  label="Total cash balance loaded"
                  value={fmtMoneyCents(cashLoadedCents)}
                />
                <Row
                  label="Total promo balance"
                  value={fmtMoneyCents(
                    event.rfid_assets_aggregate.aggregate?.sum?.promo_balance
                  )}
                />
              </>
            ),
          },
        ]}
      />

      {/* Locations by vendor */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 print:shadow-none print:border-gray-300 break-inside-avoid">
        <button
          type="button"
          onClick={() => setLocationsExpanded((v) => !v)}
          className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-red-700 transition-colors print:pointer-events-none [&[aria-expanded='true']]:mb-3"
          aria-expanded={locationsExpanded}
        >
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${locationsExpanded ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          <span>Locations by vendor</span>
          <span className="text-xs font-normal text-gray-400">
            ({locationsByVendor.length} vendor{locationsByVendor.length === 1 ? "" : "s"})
          </span>
          {flaggedVendorCount > 0 && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              {flaggedVendorCount} to review
            </span>
          )}
        </button>
        <div className={`${locationsExpanded ? "" : "hidden"} print:block`}>
        {locationsByVendor.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-500">No locations configured.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {locationsByVendor.map((group) => {
              const activeCount = group.locations.filter(
                (l) => l.is_active
              ).length;
              const status = groupStatuses.get(group.vendorId)!;
              const tone = TONE_STYLES[status.tone];
              return (
                <div
                  key={group.vendorId}
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:border-red-500 transition-colors print:hover:border-gray-200"
                >
                  <div
                    className={`flex items-center justify-between gap-4 px-4 py-3 border-b ${tone.header}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-semibold text-gray-900 text-base">
                        <span className="truncate">{group.vendorName}</span>
                        <span
                          className={`shrink-0 rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ${tone.chip}`}
                        >
                          {tone.label}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {group.vendorId ? `#${group.vendorId}` : "no vendor id"}
                        {" · "}
                        {group.locations.length} location
                        {group.locations.length === 1 ? "" : "s"}
                        {" · "}
                        {activeCount} active
                      </div>
                      {status.reasons.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {status.reasons.map((reason) => (
                            <li
                              key={reason}
                              className="text-xs font-medium text-amber-800"
                            >
                              ⚠ {reason}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {group.vendorId !== 0 && (
                      <Link
                        to={`/vendors/${group.vendorId}/edit`}
                        className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors print:hidden"
                      >
                        View vendor
                      </Link>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100 bg-white">
                    {group.locations.map((loc) => (
                      <div
                        key={loc.id}
                        className="flex items-center justify-between px-4 py-2.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {loc.name}
                          </span>
                          <span className="text-xs text-gray-400">
                            #{loc.id}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* The processor config on this location. None is worth a
                              visible flag, not a dash: the reports then attribute the
                              location to the event's primary processor. */}
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              loc.payment_processor_config
                                ? "bg-blue-50 text-blue-800"
                                : "bg-amber-50 text-amber-800"
                            }`}
                            title={
                              loc.payment_processor_config
                                ? `Payment processor config #${loc.payment_processor_config.id}`
                                : "No processor on this location"
                            }
                          >
                            {locationProcessorLabel(loc.payment_processor_config)}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              loc.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {loc.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </section>
    </>
  );
};

// ===== Plain-text "copy summary" =====
const buildPlainText = (
  event: EventSummaryData,
  fromParam: string,
  toParam: string
) => {
  const lines: string[] = [];
  const ln = (label: string, value: string | number) =>
    lines.push(`${label}: ${value}`);

  lines.push(`EVENT SUMMARY — ${event.name} (#${event.id})`);
  lines.push("=".repeat(60));
  ln("Status", event.is_active ? "Active" : "Inactive");
  ln(
    "Organization",
    event.organization
      ? `${event.organization.name} (#${event.organization.id})`
      : "—"
  );
  ln(
    "Configured dates",
    `${fmtDate(event.start_date)} → ${fmtDate(event.end_date)} (${
      event.timezone ?? "—"
    })`
  );
  ln("Date range scoped", `${fmtDate(fromParam)} → ${fmtDate(toParam)}`);
  ln("Currency", event.currency ?? "—");
  ln("Address", buildAddress(event));

  lines.push("\nPAYOUT & FEES");
  lines.push("-".repeat(60));
  ln(
    "Primary processor",
    event.payment_processor_config
      ? `${event.payment_processor_config.processor ?? "—"} (#${
          event.payment_processor_config.id
        })`
      : "—"
  );
  ln(
    "Processing rate",
    fmtPercent(event.payment_processor_config?.processing_rate ?? null)
  );
  ln("Web surcharge", fmtPercent(event.web_surcharge_percentage));
  ln(
    "Ticketing service fee / order",
    fmtMoneyDollars(event.ticketing_service_fee_per_order)
  );
  ln(
    "Top-up service fee / order",
    fmtMoneyDollars(event.top_up_service_fee_per_order)
  );
  ln("Top-up service fee %", fmtPercent(event.top_up_service_fee_percentage));

  lines.push("\nCASHLESS CONFIG");
  lines.push("-".repeat(60));
  ln("Top-up sales enabled", fmtBool(event.cashless_top_up_sales));
  ln("Open-loop cashless", fmtBool(event.open_loop_cashless));
  ln("Available tokens", tokenLabel(event.available_tokens));
  ln("Redeemable tokens", tokenLabel(event.redeemable_tokens));

  lines.push("\nCASHLESS ASSETS IN DATE RANGE");
  lines.push("-".repeat(60));
  ln("Total assets", event.rfid_assets_aggregate.aggregate?.count ?? 0);
  ln("Active", event.rfid_active.aggregate?.count ?? 0);
  ln("QR-based", event.rfid_qr.aggregate?.count ?? 0);
  ln("Access-only", event.rfid_access_only.aggregate?.count ?? 0);
  ln(
    "Total cash balance loaded",
    fmtMoneyCents(event.rfid_assets_aggregate.aggregate?.sum?.cash_balance)
  );
  ln(
    "Total promo balance",
    fmtMoneyCents(event.rfid_assets_aggregate.aggregate?.sum?.promo_balance)
  );

  lines.push("\nLOCATIONS BY VENDOR");
  lines.push("-".repeat(60));
  const byVendor = new Map<
    number,
    { name: string; locations: typeof event.locations }
  >();
  for (const loc of event.locations) {
    const vid = loc.vendor_id ?? 0;
    const vname = loc.vendor?.name ?? "(no vendor assigned)";
    if (!byVendor.has(vid)) byVendor.set(vid, { name: vname, locations: [] });
    byVendor.get(vid)!.locations.push(loc);
  }
  for (const [vid, group] of Array.from(byVendor.entries()).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  )) {
    lines.push(
      `\n  ${group.name} (#${vid || "—"}) — ${group.locations.length} location${
        group.locations.length === 1 ? "" : "s"
      }`
    );
    for (const loc of group.locations) {
      lines.push(
        `    • ${loc.name} (#${loc.id}) — ${locationProcessorLabel(
          loc.payment_processor_config
        )}${loc.is_active ? "" : " [inactive]"}`
      );
    }
  }

  return lines.join("\n");
};
